import _ from "lodash";
import React from "react";

// Components
import CriteriaRubricAssessment from "AppComponents/CriteriaRubricAssessment";
import I18nHOC from "UIComponents/I18nHOC";
import DPAssessmentRubric from "AppComponents/DPAssessmentRubric";

// External libs.
import { connect } from "react-redux";
import { compose } from "lib/apolloClient";

// Module
import {
  createDPRubricRatings,
  deleteDPRubricRatings,
} from "AssessmentEvaluation/modules/AssessmentEvaluationModule";
import { updateBulkAssignmentGradeRatings } from "ClassRoom/modules/ClassRoomModule";

// Utils
import {
  getAssessmentToolText,
  getAssessmentToolSubtext,
} from "ClassRoom/modules/utils";

// Scss
import classes from "./CriteriaRubricAssessmentWrapper.scss";

const styles = {
  assessmentContainerStyle: {
    minWidth: "100%",
  },
};

const CriteriaRubricAssessmentWrapper = props => {
  const {
    t,
    criteria = [],
    ibPYPElementRatings = [],
    parentId,
    parentType = "STUDENT_ASSIGNMENT",
    studentId,
    isEvaluated,
    typeOfScale = "evaluationScale",
    isEvaluationEnable,
    createDPRubricRatings,
    deleteDPRubricRatings,
    userType,
    isSharedWithStudent,
    assessmentTool,
    sourceFrom,
    isSplitView = false,
    customHeaderLabelClass,
    ibPYPElementRatingsLocal,
    setIBPYPElementRatingsLocal,
    academicTermId,
    isAutoSaveDisabled,
    assessmentLeafNodLabelStyles,
    leafNodLabelStyles,
    accordianHeaderStyle,
    showToolIcon = false,
    customSelectedRatingElement,
    customRatingElementClass,
    marksLabelLocale,
    showRevampedUI = false,
    isAiEvaluationEnabled,
    onAcceptAIEvaluation,
    isGeneratingAIResponse,
    assessmentId,
    assessmentToolId,
    assignmentId,
    isWorksheetScorebook,
    assessmentFields,
  } = props;

  const mode = isEvaluationEnable ? "edit" : "view";
  const isStudentAssessmentsSummary =
    sourceFrom === "STUDENT_ASSESSMENTS_SUMMARY";

  const onSelectRubricAssessmentCriteriaRating = ({
    mark,
    isSelected = false,
    id,
  }) => {
    if (mode === "view" || userType !== "staff") return;

    const isAISelectedCriteria = _.find(ibPYPElementRatings, {
      ibPYPElementId: id,
      isDeleted: false,
      creatorType: "AI",
    });

    const params = {
      parentId,
      parentType,
      ibPYPElementId: id,
      studentId,
      academicTermId,
      ibPYPElementType: "DP_CRITERIA",
      ibPYPElementRatingsLocal,
      setIBPYPElementRatingsLocal,
      isAutoSaveDisabled,
    };

    const isDeleteAiRatings = isAiEvaluationEnabled && isAISelectedCriteria;

    if (!isSelected || isAISelectedCriteria) {
      if (isDeleteAiRatings) {
        onAcceptAIEvaluation({
          ibPypElementId: id,
          academicTermId,
          ibPYPElementType: "DP_CRITERIA",
          value: _.toString(mark),
        });
      }
      createDPRubricRatings({
        ...params,
        isDeleteAiRatings,
        isPublished: isEvaluated,
        value: _.toString(mark),
        source: sourceFrom,
        isWorksheetScorebook,
      });
    } else {
      deleteDPRubricRatings(params);
    }
  };

  const getRatedCriteria = () => {
    const criteriaIds = _.map(criteria, "id");

    let ratedCriteria = _.filter(ibPYPElementRatings, rating => {
      return (
        _.get(rating, "ibPYPElementType") === "DP_CRITERIA" &&
        _.includes(criteriaIds, _.get(rating, "ibPYPElementId")) &&
        !rating?.isDeleted
      );
    });

    if (isAiEvaluationEnabled) {
      // if ai rating is present, then show ai rating
      ratedCriteria = _.filter(ratedCriteria, rating => {
        if (rating.creatorType === "AI") {
          return true;
        }

        // if for a criteria, both staff and ai rating is present,
        // don't consider the staff rating in the rated criteria array
        const staffHasAiRating = _.find(ibPYPElementRatings, {
          ibPYPElementId: rating.ibPYPElementId,
          creatorType: "AI",
          isDeleted: false,
        });

        return !staffHasAiRating;
      });
    } else {
      // staff rating should be considered, if both ai and staff rating is present
      ratedCriteria = _.filter(ratedCriteria, rating => {
        if (rating.creatorType === "STAFF") {
          return true;
        }

        // if for a criteria, both staff and ai rating is present,
        // don't consider the ai rating in the rated criteria array
        const aiHasStaffRating = _.find(ibPYPElementRatings, {
          ibPYPElementId: rating.ibPYPElementId,
          creatorType: "STAFF",
        });

        return !aiHasStaffRating;
      });
    }

    ratedCriteria = _.map(ratedCriteria, rating => {
      return {
        ibPYPElementId: rating?.ibPYPElementId,
        value: rating?.value,
        creatorType: rating?.creatorType,
      };
    });

    if (
      (userType === "staff" && !isStudentAssessmentsSummary) ||
      isSharedWithStudent
    ) {
      return ratedCriteria;
    } else {
      return [];
    }
  };

  const { assessmentToolText: headerLabel } = getAssessmentToolText({
    t,
    assessmentToolType: "DP_ASSESSMENT_RUBRIC",
    assessmentTool,
  });

  const subText = getAssessmentToolSubtext({ assessmentTool, t });
  const tags = _.get(assessmentTool, "presetRubric.genericTags", []);
  const criteriaData = [
    {
      criteria: criteria,
      headerLabel,
      headerSubText: subText,
      hasMarksBox: true,
      tags,
    },
  ];

  if (isSplitView) {
    return (
      <DPAssessmentRubric
        {...props}
        tags={tags}
        ratedCriteria={getRatedCriteria()}
        assessmentToolItem={assessmentTool}
        criteria={criteria}
        assessmentHeading={_.get(assessmentTool, "label", "")}
        assessmentType={subText}
        sourceFrom={sourceFrom}
      />
    );
  }

  return (
    <CriteriaRubricAssessment
      onSelectMark={onSelectRubricAssessmentCriteriaRating}
      criteriaData={criteriaData}
      ratedCriteria={getRatedCriteria()}
      assessmentContainerStyle={styles.assessmentContainerStyle}
      isEditable={mode !== "view" && userType === "staff"}
      typeOfScale={typeOfScale}
      assessmentToolId={assessmentToolId}
      isEvaluationEnable={isEvaluationEnable}
      criteriaLabel={t("common:total")}
      assessmentHeading={_.get(assessmentTool, "label", "")}
      customHeaderLabelClass={
        customHeaderLabelClass || classes.customHeaderLabelClass
      }
      ibPYPElementRatings={ibPYPElementRatings}
      sourceFrom={"CLASSROOM"}
      studentId={studentId}
      isEvaluated={isEvaluated}
      assessmentLeafNodLabelStyles={assessmentLeafNodLabelStyles}
      leafNodLabelStyles={leafNodLabelStyles}
      accordianHeaderStyle={accordianHeaderStyle}
      showToolIcon={showToolIcon}
      customSelectedRatingElement={customSelectedRatingElement}
      customRatingElementClass={customRatingElementClass}
      marksLabelLocale={marksLabelLocale}
      showRevampedUI={showRevampedUI}
      academicTermId={academicTermId}
      parentId={parentId}
      assessmentId={assessmentId}
      assignmentId={assignmentId}
      assessmentFields={assessmentFields}
      isAiEvaluationEnabled={isAiEvaluationEnabled}
      isGeneratingAIResponse={isGeneratingAIResponse}
      onAcceptAIEvaluation={onAcceptAIEvaluation}
    />
  );
};

const mapStateToProps = state => {
  return {
    userType: state.login.userInfo.user_type,
  };
};

const mapActionCreators = {
  createDPRubricRatings,
  deleteDPRubricRatings,
  updateBulkAssignmentGradeRatings,
};

export default compose(
  I18nHOC({ resource: ["common"] }),
  connect(mapStateToProps, mapActionCreators)
)(CriteriaRubricAssessmentWrapper);
